import React, { useEffect, useState } from 'react';

import { Loader } from '@bharatpe/bharat-ui';
import Toaster from 'components/atoms/Toaster';
import Routes from 'Routes';
import {
	getURLParameter,
	initNativeSupport,
	openDeepLinkWithoutCallback,
	openDeepLinkWithCallback,
	closeNativeSupport,
} from 'utils/nativeSupport';
import { useDispatch } from 'react-redux';

import { initImmediateNativeSupport } from 'utils/Utils';
import history from 'utils/history';
import { removeState, saveState, loadState } from 'utils/LocalStorage';
import {
	LANGUAGE,
	TOKEN_SESSION_KEY,
	MODULE_DEEPLINKS,
	getFrmChargebackListDeeplink,
	getFrmDisputeDetailsDeeplink,
} from 'utils/CONSTANT';
import { ILanguage } from 'types';
import i18next from 'i18n';
import { fetchMerchantInfo, useMerchantInfo } from 'Slices';

import '@bharatpe/bharat-ui/web/styles.min.css';
import './styles/index.scss';
import { DummyPageShimmer } from 'components/molecules/Shimmer';

initImmediateNativeSupport();
const pageRoute = getURLParameter(window.location.href, 'pageRoute') || '';
const orderIdStatus = getURLParameter(window.location.href, 'order_id') || '';
const serialNumber = getURLParameter(window.location.href, 'serialNumber') || '';
const isOtView = getURLParameter(window.location.href, 'isOtView') || '';
const applicationId = getURLParameter(window.location.href, 'applicationId') || '';
// const language = getURLParameter(window.location.href, 'language');

removeState('v2OrderFlow', true);
removeState('returnedFromPg', true);
removeState('redirectToSwipe', true);
removeState('isNewPlanValid', true);
removeState('redirectFrom', true);
removeState('startingStage', true);
removeState('isUpgradeFlowModalOpened', true);
removeState('isNewOnBoardingPolicyEligible', true);
if (orderIdStatus) {
	saveState('v2OrderFlow', true, true);
	saveState('returnedFromPg', true, true);
	history.push(`/order-details/${orderIdStatus}`);
}

switch (pageRoute) {
	case 'v2-order-flow':
		saveState('v2OrderFlow', true, true);
		history.push('/V2/landing');
		break;
	case 'bp-one-landing':
		saveState('v2OrderFlow', true, true);
		history.push('/bharatpe-one-plan');
		break;
	case 'swipe-migration':
		history.push('/swipe-migration-landing');
		break;
	case 'order-flow':
		saveState('redirectToSwipe', true, true);
		break;
	case 'transactions':
		history.push('/transactions-list');
		break;
	case 'my-charges':
		history.push('/select-device-my-charges/home');
		break;
	case 'statement':
		history.push('/statement/pd');
		break;
	case 'transaction-details':
		history.push(`/transaction-details/pd/${getURLParameter(window.location.href, 'trnxnId')}`);
		break;
	// chargeback + dispute-details: FRM deeplinks run in onLoad after initNativeSupport (not here; native bridge is not ready at module import).
	case 'returnDevice':
		history.push(`/return/0/status/${serialNumber}`);
		break;
	case 'deactivationStatus':
		history.push(`/deactivate/0/status/${serialNumber}`);
		break;
	case 'returnDeviceListing':
		history.push('/return/listing');
		break;
	case 'overdue':
		history.push('/overdue-list');
		break;
	case 'migration':
		history.push('/swipe-migration');
		break;
	case 'rentalCashback':
		history.push('/rental-cashback-details');
		break;
	case 'refund-status':
		history.push('/cancellation-refund-status');
		break;
	case 'updateRanking':
		history.push('/update-ranking');
		break;
	case 'serviceRequest':
		history.push('/service-request');
		break;
	case 'emiSteps':
		history.push('/emi-steps');
		break;
	// dispute-details: FRM deeplink in onLoad; do not history.push to /upload-chargeback
	case 'lifetimeFeeCashback':
		history.push(`/lifetime-fee-cashback`);
		break;
	case 'referEarn':
		history.push('/refer-earn');
		break;
	case 'planDetails':
		saveState('v2OrderFlow', true, true);
		history.push(`/plan-details/${applicationId}`);
		break;
	case 'serviceRequestListing':
		history.push('/service-request-listing');
		break;
	case 'swipeOrdersListing':
		history.push('/swipe-orders-listing');
		break;
	case 'swipe-pending-migration-landing':
		history.push('/swipe-pending-migration-landing');
		break;
	default:
		break;
}

const App: React.FC = () => {
	const dispatch = useDispatch();
	const merchantInfo = useMerchantInfo();
	const [appInfoLoaded, setAppInfoLoaded] = useState(false);
	// const [selectedLanguage, setSelectedLanguage] = useState((loadState(LANGUAGE, true) as ILanguage) ?? 'en');

	useEffect(() => {
		onLoad();
		// handleUpdateLocalization(language as ILanguage);
	}, []);

	// useEffect(() => {
	// 	i18next.changeLanguage(selectedLanguage);
	// }, [selectedLanguage]);

	useEffect(() => {
		if (merchantInfo?.data?.localization) {
			handleUpdateLocalization(merchantInfo?.data?.localization);
		}
		const pageRouteForRedirect =
			getURLParameter(window.location.href, 'pageRoute') || getURLParameter(window.location.hash, 'pageRoute') || '';
		if (
			merchantInfo?.data?.merchantType === 'ORGANIZED' &&
			!isOtView &&
			pageRouteForRedirect !== 'chargeback' &&
			pageRouteForRedirect !== 'dispute-details'
		) {
			openDeepLinkWithoutCallback(`${MODULE_DEEPLINKS.PAYMENT_DASHBOARD}?wroute=swipe`);
		}
	}, [merchantInfo]);

	const onLoad = async () => {
		if (isOtView) {
			const finalToken =
				getURLParameter(window.location.href, 'token') || getURLParameter(window.location.href, 'visa');
			saveState(TOKEN_SESSION_KEY, finalToken, true);
		} else {
			await initNativeSupport();
		}
		// FRM: must run after native init; module-level openDeepLink is too early and never reaches the app shell.
		const pageRouteForFrm =
			getURLParameter(window.location.href, 'pageRoute') || getURLParameter(window.location.hash, 'pageRoute') || '';
		const paymentTxnIdForFrm =
			getURLParameter(window.location.href, 'paymentTxnId') ||
			getURLParameter(window.location.hash, 'paymentTxnId') ||
			'';
		const sourceProductForFrm =
			getURLParameter(window.location.href, 'sourceProduct') ||
			getURLParameter(window.location.hash, 'sourceProduct') ||
			'SWIPE';
		if (pageRouteForFrm === 'chargeback') {
			const url = getFrmChargebackListDeeplink(sourceProductForFrm);
			// eslint-disable-next-line no-console
			console.log('[FRM] chargeback: invoking openDeepLinkWithCallback', url);
			openDeepLinkWithCallback(url, () => {
				// eslint-disable-next-line no-console
				console.log('[FRM] chargeback: native deep link flow completed (promise resolved)');
				closeNativeSupport();
			});
		} else if (pageRouteForFrm === 'dispute-details') {
			const url = getFrmDisputeDetailsDeeplink(paymentTxnIdForFrm, sourceProductForFrm);
			// eslint-disable-next-line no-console
			console.log('[FRM] dispute-details: invoking openDeepLinkWithCallback', url);
			openDeepLinkWithCallback(url, () => {
				// eslint-disable-next-line no-console
				console.log('[FRM] dispute-details: native deep link flow completed (promise resolved)');
				closeNativeSupport();
			});
		}
		await dispatch(fetchMerchantInfo({}));
		setAppInfoLoaded(true);
	};

	const handleUpdateLocalization = (localization: ILanguage) => {
		saveState(LANGUAGE, localization, true);
		// setSelectedLanguage(localization);
		i18next.changeLanguage(localization);
	};

	const getLoader = () => <DummyPageShimmer />;

	const getRouter = () => <Routes />;

	return (
		<div className="dark">
			{appInfoLoaded
				? merchantInfo?.data?.merchantType !== 'ORGANIZED' || isOtView
					? getRouter()
					: getLoader()
				: getLoader()}
			<Toaster />
		</div>
	);
};

export default App;
